/**
 * PlatformStatusChecklist Component (Story 5.11, Task 5.4)
 *
 * Shows per-platform loading status during report data population.
 * Displays platform display names with status icons:
 * - Checkmark for success
 * - Spinner for loading
 * - Warning icon for failed/missing
 *
 * Stagger checkmark animations for UX polish even though data arrives all at once.
 */

import { Check, Warning, CircleNotch } from "@phosphor-icons/react";
import type { PlatformStatus } from "../../hooks/useReportData";

const PLATFORM_DISPLAY_NAMES: Record<string, string> = {
  "google-analytics-4": "Google Analytics 4",
  "google-search-console": "Google Search Console",
  "google-ads": "Google Ads",
  "youtube-analytics": "YouTube Analytics",
  "google-business-profile": "Google Business Profile",
  "google-pagespeed": "Google PageSpeed",
};

function getDisplayName(platform: string): string {
  return PLATFORM_DISPLAY_NAMES[platform] ?? platform;
}

interface PlatformStatusChecklistProps {
  platformStatus: Record<string, PlatformStatus>;
  isLoading: boolean;
}

export function PlatformStatusChecklist({
  platformStatus,
  isLoading,
}: PlatformStatusChecklistProps) {
  const platforms = Object.entries(platformStatus);

  if (platforms.length === 0 && !isLoading) return null;

  return (
    <div
      className="mb-4 rounded-lg border bg-muted/50 px-4 py-3"
      data-testid="platform-status-checklist"
      aria-live="polite"
    >
      <p className="text-sm font-medium text-muted-foreground mb-2">
        Data Sources
      </p>
      <ul className="space-y-1.5">
        {isLoading && platforms.length === 0 && (
          <li className="flex items-center gap-2 text-sm text-muted-foreground">
            <CircleNotch size={16} className="animate-spin" />
            Loading integration data...
          </li>
        )}
        {platforms.map(([platform, status], index) => (
          <li
            key={platform}
            className="flex items-center gap-2 text-sm"
            role="status"
            aria-label={`${getDisplayName(platform)} ${
              status.status === "success"
                ? "loaded"
                : status.status === "error"
                  ? "failed"
                  : "not connected"
            }`}
            style={{
              // Stagger animation delay for checkmarks
              animationDelay:
                status.status === "success" ? `${index * 150}ms` : undefined,
            }}
          >
            {status.status === "success" && (
              <Check
                size={16}
                weight="bold"
                className="text-green-600 animate-in fade-in"
                style={{ animationDelay: `${index * 150}ms` }}
              />
            )}
            {status.status === "error" && (
              <Warning size={16} weight="bold" className="text-amber-500" />
            )}
            {status.status === "missing" && (
              <Warning size={16} weight="bold" className="text-red-500" />
            )}
            <span
              className={
                status.status === "success"
                  ? "text-foreground"
                  : "text-muted-foreground"
              }
            >
              {getDisplayName(platform)}
            </span>
            {status.fromCache && status.status === "success" && (
              <span className="text-xs text-muted-foreground">(cached)</span>
            )}
            {status.error && (
              <span className="text-xs text-red-500 ml-1">
                — {status.error}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
